import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';

/* ──────────────────────────────────────────────────────────────────────────
 * Ordens de Servico (OS) — domino manufacturing.
 *
 * Contratos espelhados do backend:
 *   backend/apps/api/src/features/manufacturing/presentation/http/dto/manufacturing-extended.dto.ts
 *   backend/apps/api/src/features/manufacturing/presentation/http/controllers/service-orders.controller.ts
 *
 * Rotas consumidas (prefixo /api/v1):
 *   GET  /manufacturing/service-orders        -> lista paginada (filtros)
 *   GET  /manufacturing/service-orders/:id    -> detalhe agregado (timeEntries,
 *                                                materials, comments)
 *   POST /manufacturing/service-orders/:id/time-entries -> apontamento de horas
 *   POST /manufacturing/service-orders/:id/materials    -> materiais e pecas
 *   POST /manufacturing/service-orders/:id/comments     -> historico/comentarios
 * ────────────────────────────────────────────────────────────────────────── */

const SERVICE_ORDERS_KEY = ['manufacturing', 'service-orders'] as const;

/* ── Tipos (DTOs reais do backend) ── */

export type ServiceOrderStatus =
  | 'DRAFT'
  | 'OPEN'
  | 'IN_PROGRESS'
  | 'WAITING_PART'
  | 'COMPLETED'
  | 'CANCELLED';

export type ServiceOrderPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';

export type ServiceOrderType =
  | 'PREVENTIVE'
  | 'CORRECTIVE'
  | 'INSPECTION'
  | 'INSTALLATION';

/** Espelha `ServiceOrderResponseDto`. */
export interface ServiceOrder {
  id: string;
  number: string;
  title: string;
  description?: string | null;
  status: ServiceOrderStatus;
  priority: ServiceOrderPriority;
  type: ServiceOrderType;
  customerRef?: string | null;
  equipmentRef?: string | null;
  plannedStart?: string | null;
  plannedEnd?: string | null;
  actualStart?: string | null;
  actualEnd?: string | null;
  totalHours: number;
  totalMaterialCost: number;
  createdAt: string;
  updatedAt: string;
}

/** Espelha `ServiceOrderTimeEntryResponseDto`. */
export interface ServiceOrderTimeEntry {
  id: string;
  employeeName: string;
  occurredAt: string;
  hours: number;
  description?: string | null;
}

/** Espelha `ServiceOrderMaterialResponseDto`. */
export interface ServiceOrderMaterial {
  id: string;
  productId: string;
  description?: string | null;
  quantity: number;
  unit: string;
  unitCost: number;
  totalCost: number;
  usedAt: string;
}

/** Espelha `ServiceOrderCommentResponseDto`. */
export interface ServiceOrderComment {
  id: string;
  authorName: string;
  body: string;
  createdAt: string;
}

/** Espelha `ServiceOrderDetailResponseDto` — agregado da OS. */
export interface ServiceOrderDetail extends ServiceOrder {
  timeEntries: ServiceOrderTimeEntry[];
  materials: ServiceOrderMaterial[];
  comments: ServiceOrderComment[];
}

/** Envelope paginado (`PaginatedResponseDto<T>`). */
export interface ServiceOrderList {
  data: ServiceOrder[];
  total: number;
  page: number;
  limit: number;
}

/* ── Inputs dos POSTs ── */

export interface AddTimeEntryInput {
  employeeName: string;
  hours: number;
  occurredAt?: string;
  description?: string;
}

/** Resposta do POST time-entries: `{ timeEntryId, totalHours }`. */
export interface AddTimeEntryResult {
  timeEntryId: string;
  totalHours: number;
}

export interface AddMaterialInput {
  productId: string;
  description?: string;
  quantity: number;
  unit: string;
  unitCost: number;
  usedAt?: string;
}

/** Resposta do POST materials: `{ materialId, totalCost, totalMaterialCost }`. */
export interface AddMaterialResult {
  materialId: string;
  totalCost: number;
  totalMaterialCost: number;
}

export interface AddCommentInput {
  authorName: string;
  body: string;
}

/** Resposta do POST comments: `{ commentId, createdAt }`. */
export interface AddCommentResult {
  commentId: string;
  createdAt: string;
}

/* ── Rótulos e variantes de apresentação ── */

export const SERVICE_ORDER_STATUS_LABEL: Record<ServiceOrderStatus, string> = {
  DRAFT: 'Rascunho',
  OPEN: 'Aberta',
  IN_PROGRESS: 'Em Andamento',
  WAITING_PART: 'Aguardando Peça',
  COMPLETED: 'Concluída',
  CANCELLED: 'Cancelada',
};

export const SERVICE_ORDER_STATUS_VARIANT: Record<
  ServiceOrderStatus,
  'neutral' | 'info' | 'warning' | 'success' | 'error' | 'processing'
> = {
  DRAFT: 'neutral',
  OPEN: 'info',
  IN_PROGRESS: 'warning',
  WAITING_PART: 'processing',
  COMPLETED: 'success',
  CANCELLED: 'error',
};

export const SERVICE_ORDER_PRIORITY_LABEL: Record<ServiceOrderPriority, string> = {
  LOW: 'Baixa',
  MEDIUM: 'Média',
  HIGH: 'Alta',
  URGENT: 'Urgente',
};

export const SERVICE_ORDER_PRIORITY_VARIANT: Record<
  ServiceOrderPriority,
  'neutral' | 'info' | 'warning' | 'success' | 'error'
> = {
  LOW: 'neutral',
  MEDIUM: 'info',
  HIGH: 'warning',
  URGENT: 'error',
};

export const SERVICE_ORDER_TYPE_LABEL: Record<ServiceOrderType, string> = {
  PREVENTIVE: 'Preventiva',
  CORRECTIVE: 'Corretiva',
  INSPECTION: 'Inspeção',
  INSTALLATION: 'Instalação',
};

/** `4.5h` / `12h` — horas apontadas de uma OS. */
export function formatHours(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}h`;
}

/* ── Queries ── */

export function useServiceOrders(params?: {
  status?: string;
  search?: string;
  equipmentRef?: string;
  customerRef?: string;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: [...SERVICE_ORDERS_KEY, params],
    queryFn: () =>
      apiClient.get<ServiceOrderList>('/manufacturing/service-orders', {
        params: params as Record<string, string>,
      }),
  });
}

export function useServiceOrder(id: string | undefined) {
  return useQuery({
    queryKey: [...SERVICE_ORDERS_KEY, id],
    queryFn: () => apiClient.get<ServiceOrderDetail>(`/manufacturing/service-orders/${id}`),
    enabled: !!id,
  });
}

/* ── Mutations ── */

export function useAddServiceOrderTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & AddTimeEntryInput) =>
      apiClient.post<AddTimeEntryResult>(
        `/manufacturing/service-orders/${id}/time-entries`,
        input,
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: [...SERVICE_ORDERS_KEY, vars.id] });
    },
  });
}

export function useAddServiceOrderMaterial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & AddMaterialInput) =>
      apiClient.post<AddMaterialResult>(`/manufacturing/service-orders/${id}/materials`, input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: [...SERVICE_ORDERS_KEY, vars.id] });
    },
  });
}

export function useAddServiceOrderComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & AddCommentInput) =>
      apiClient.post<AddCommentResult>(`/manufacturing/service-orders/${id}/comments`, input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: [...SERVICE_ORDERS_KEY, vars.id] });
    },
  });
}